/** Storage dialogs on the shared Modal: create folder, move selected items, confirm a destructive action */

import { useId, useMemo, useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { useT } from '../../i18n'
import { Modal } from '../ui/modal'
import { buttonDangerClass, buttonPrimaryClass, buttonSecondaryClass, errorTextClass, hintClass, inputClass, labelClass } from '../ui/styles'
import { useCreateStorageFolder, type StorageFolderNode } from '../../hooks/use-storage'
import { moveTargetOptions } from './storage-tree-utils'

const errorMessage = (err: unknown) => (err instanceof Error ? err.message : String(err))

interface CreateFolderDialogProps {
  /** Folder the new one is created in (null = top level) */
  parentId: string | null
  /** Display name of the parent for the hint */
  parentName: string
  onClose: () => void
}

export function CreateFolderDialog({ parentId, parentName, onClose }: CreateFolderDialogProps) {
  const t = useT()
  const id = useId()
  const createFolder = useCreateStorageFolder()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) {
      setError(t('storage.folder.nameRequired'))
      return
    }
    setError(null)
    try {
      await createFolder.mutateAsync({ name: trimmed, parentId })
      toast.success(t('storage.folder.created', { name: trimmed }))
      onClose()
    } catch (err) {
      setError(t('storage.error.generic', { message: errorMessage(err) }))
    }
  }

  return (
    <Modal title={t('storage.folder.new')} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <label htmlFor={`${id}-name`} className={labelClass}>
            {t('storage.folder.name')}
          </label>
          <input id={`${id}-name`} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required className={inputClass} />
          <p className={hintClass}>{t('storage.folder.createdIn', { name: parentName })}</p>
        </div>
        {error && (
          <p role="alert" className={errorTextClass}>
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className={buttonSecondaryClass}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={createFolder.isPending} className={buttonPrimaryClass}>
            {createFolder.isPending ? t('storage.folder.creating') : t('storage.folder.create')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

/** Sentinel select value for the storage root (folder ids are never empty) */
const ROOT_VALUE = ''

interface MoveItemsDialogProps {
  tree: StorageFolderNode[]
  /** Folder currently open (moving there is a no-op) */
  currentFolderId: string | null
  folderIds: string[]
  itemCount: number
  pending: boolean
  onClose: () => void
  onMove: (targetFolderId: string | null) => void
}

export function MoveItemsDialog({ tree, currentFolderId, folderIds, itemCount, pending, onClose, onMove }: MoveItemsDialogProps) {
  const t = useT()
  const id = useId()
  const options = useMemo(() => moveTargetOptions(tree, folderIds), [tree, folderIds])
  const currentValue = currentFolderId ?? ROOT_VALUE
  const [target, setTarget] = useState<string>(() => {
    if (currentValue !== ROOT_VALUE) return ROOT_VALUE
    return options[0]?.id ?? ROOT_VALUE
  })
  const isNoop = target === currentValue

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isNoop) return
    onMove(target === ROOT_VALUE ? null : target)
  }

  return (
    <Modal title={t('storage.move.title', { count: itemCount })} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <label htmlFor={`${id}-target`} className={labelClass}>
            {t('storage.move.target')}
          </label>
          <select id={`${id}-target`} value={target} onChange={(e) => setTarget(e.target.value)} className={inputClass}>
            <option value={ROOT_VALUE}>{t('storage.root')}</option>
            {options.map((o) => (
              // Non-breaking spaces keep the indentation visible inside native <option>s
              <option key={o.id} value={o.id}>
                {'  '.repeat(o.depth + 1)}
                {o.name}
              </option>
            ))}
          </select>
          {isNoop && <p className={hintClass}>{t('storage.move.alreadyHere')}</p>}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className={buttonSecondaryClass}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={pending || isNoop} className={buttonPrimaryClass}>
            {pending ? t('storage.move.moving') : t('storage.move.submit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

interface ConfirmDeleteDialogProps {
  title: string
  description: string
  confirmLabel: string
  pending?: boolean
  onClose: () => void
  onConfirm: () => void
}

/** Destructive confirmation; the confirm button gets focus so Enter confirms and Escape cancels */
export function ConfirmDeleteDialog({ title, description, confirmLabel, pending, onClose, onConfirm }: ConfirmDeleteDialogProps) {
  const t = useT()
  return (
    <Modal title={title} onClose={onClose}>
      <p className="text-sm text-fg-muted">{description}</p>
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onClose} className={buttonSecondaryClass}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          data-autofocus
          onClick={onConfirm}
          disabled={pending}
          className={buttonDangerClass}
        >
          {pending ? t('storage.delete.deleting') : confirmLabel}
        </button>
      </div>
    </Modal>
  )
}
